import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import memoize from 'lodash/memoize';
import type { GraphQLError } from 'graphql';
import jwtDecode from 'jwt-decode';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getStatusCode } from '@confluence/error-boundary/entry-points/isStatusCodeError';
import { fg } from '@confluence/feature-gating';

import type {
	ExternalShareMediaDownloadTokenQuery as ExternalShareMediaDownloadTokenQueryT,
	ExternalShareMediaDownloadTokenQueryVariables,
} from './__types__/ExternalShareMediaDownloadTokenQuery';
import type {
	MediaDownloadTokenQuery as MediaDownloadTokenQueryT,
	MediaDownloadTokenQueryVariables,
} from './__types__/MediaDownloadTokenQuery';
import type {
	MediaUploadTokenQuery as MediaUploadTokenQueryT,
	CommentMediaUploadTokenQuery as CommentMediaUploadTokenQueryT,
	MediaUploadTokenQueryVariables,
	CommentMediaUploadTokenQueryVariables,
} from './__types__/MediaUploadTokenQuery';
import type {
	ExternalContentMediaSessionQuery as ExternalContentMediaSessionQueryT,
	ExternalContentMediaSessionQueryVariables,
} from './__types__/ExternalContentMediaSessionQuery';
import {
	type DataSecurityPolicyDecidableContentStatus,
	type DSPIsActionEnabledForContentQuery as DSPIsActionEnabledForContentQueryT,
	type DSPIsActionEnabledForContentQueryVariables,
	DataSecurityPolicyAction,
} from './__types__/DSPIsActionEnabledForContentQuery';
import { ExternalShareMediaDownloadTokenQuery } from './ExternalShareMediaDownloadTokenQuery.graphql';
import { MediaDownloadTokenQuery } from './MediaDownloadTokenQuery.graphql';
import {
	MediaUploadTokenQuery,
	CommentMediaUploadTokenQuery,
} from './MediaUploadTokenQuery.graphql';
import { DSPIsActionEnabledForContentQuery } from './DSPIsActionEnabledForContentQuery.graphql';
import { experimentalFileUploadMutate } from './experimentalFileUploadMutate';
import { ExternalContentMediaSessionQuery } from './ExternalContentMediaSessionQuery.graphql';

type setAttachmentRequestBody = {
	size: number;
	contentType: string;
	mimeType: string;
	fileName: string;
	pageId: string;
	fileStoreId: string;
	minorEdit: boolean;
	renderEditorHTML: boolean;
	collectionName: string;
};

let isFirstLoad = true;

// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
export const setAttachment = (requestBody: setAttachmentRequestBody): void | Promise<any> => {
	if (
		requestBody.contentType === 'comment' &&
		fg('confluence_comment_use_comment_media_endpoint')
	) {
		// Hard disable drag-and-drop upload functionality for comments
		return;
	}

	// Use regular mutation for normal uploads (file browser, copy-paste, etc.)
	return experimentalFileUploadMutate({ variables: requestBody });
};

// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
export const fetchMediaToken = (contentId: string) =>
	getApolloClient()
		.query<MediaDownloadTokenQueryT, MediaDownloadTokenQueryVariables>({
			query: MediaDownloadTokenQuery,
			variables: {
				contentId,
				status: ['current', 'draft', 'archived'],
				missingContentId: !contentId,
			},
			context: {
				allowOnExternalPage: true,
			},
			// If SSR has populated the cache then we want to use the cache when rendering SPA-over-SSR
			fetchPolicy: isFirstLoad ? 'cache-first' : 'network-only',
		})
		.then(
			/* onFulfilled */ (value) => {
				isFirstLoad = false;

				try {
					throttleFetchMediaTokenErrors(contentId)(value?.errors);
				} catch {}

				return value;
			},
			/* onRejected */ (reason: ApolloErrorInterop) => {
				try {
					throttleFetchMediaTokenErrors(contentId)(reason?.graphQLErrors, reason?.networkError);
				} catch {}

				throw reason;
			},
		);

/**
 * `fetchMediaToken` is called repetitively to renew the token before it
 * expires. If an error condition arises (because of frontend or backend), the
 * same error(s) may be reported as unhandled to our analytics and monitoring at
 * a very high rate/volume. Such repetitive reporting fron one user in one tab
 * is noisy. `throttleFetchMediaTokenErrors` attempts to reduce the noise.
 */
const throttleFetchMediaTokenErrors = memoize((_cacheKey: string) => {
	/**
	 * The errors that happened last time. The `GraphQLError` instances are big
	 * and are not trivial to compare so `prevErrors` caches partial info.
	 */
	let prevErrors: Record<string, string> | undefined = undefined;

	return (
		graphQLErrors?: ApolloErrorInterop['graphQLErrors'],
		networkError?: ApolloErrorInterop['networkError'],
	) => {
		// Each of `graphQLErrors` and `networkError` are reported to analytics and
		// monitoring so type of `Error` does not matter to what follows and
		// `GraphQLError` is the most convenient TypeScript type to deal with.
		if (networkError) {
			graphQLErrors = (graphQLErrors || []).concat(networkError as GraphQLError);
		}

		if (graphQLErrors) {
			/**
			 * The new/current errors which will become `prevErrors` at the end of
			 * this iteration.
			 */
			const nextErrors = {};

			for (const graphQLError of graphQLErrors) {
				// Extract the partial, easy-to-compare, cachable info from the big
				// `GraphQLError` instance:
				const message =
					graphQLError.message ||
					String(graphQLError) ||
					graphQLError.originalError?.message ||
					String(graphQLError.originalError);
				const { path } = graphQLError;
				const key = (Array.isArray(path) && path.join('.')) || message;
				if (key) {
					const value = getStatusCode(graphQLError) || message;
					if (value) {
						nextErrors[key] = value;
						// If a similar error happened in the previous iteration, do not
						// report it to analytics and monitoring again:
						if (prevErrors?.[key] === value) {
							markErrorAsHandled(graphQLError);
						}
					}
				}
			}

			prevErrors = nextErrors;
		} else {
			// If `MediaDownloadTokenQuery` succeeds, report subsequent/future errors
			// even if similar ones occurred and were reported before the current
			// success - there is a meaningful change in state and it may be useful in
			// debugging.
			prevErrors = undefined;
		}
	};
});

type ExternalMediaTokenQuery = ExternalShareMediaDownloadTokenQueryT;
type ExternalMediaTokenQueryVariables = ExternalShareMediaDownloadTokenQueryVariables;

// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
export const fetchExternalShareMediaToken = (contentId: string) =>
	getApolloClient().query<ExternalMediaTokenQuery, ExternalMediaTokenQueryVariables>({
		query: ExternalShareMediaDownloadTokenQuery,
		variables: {
			contentId,
		},
		context: {
			allowOnExternalPage: true,
		},
		fetchPolicy: 'network-only',
	});

// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
export const fetchExternalContentMediaSessionToken = (shareToken: string) =>
	getApolloClient().query<
		ExternalContentMediaSessionQueryT,
		ExternalContentMediaSessionQueryVariables
	>({
		query: ExternalContentMediaSessionQuery,
		variables: {
			shareToken,
		},
		context: {
			allowOnExternalPage: true,
		},
		fetchPolicy: 'network-only',
	});

// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
export const getUploadTokenV0 = (contentId: string) =>
	getApolloClient().query<MediaUploadTokenQueryT, MediaUploadTokenQueryVariables>({
		query: MediaUploadTokenQuery,
		variables: {
			contentId,
		},
		fetchPolicy: 'no-cache',
	});

type JwtPayload = {
	exp: number; // Expiration time in seconds since epoch
};

export const getUploadToken = async (contentId: string) => {
	if (!fg('confluence-media-upload-token-cache')) {
		return getUploadTokenV0(contentId);
	}

	const client = getApolloClient();
	try {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const { data } = await client.query<MediaUploadTokenQueryT, MediaUploadTokenQueryVariables>({
			query: MediaUploadTokenQuery,
			variables: { contentId },
			fetchPolicy: 'cache-first',
		});
		const token = data?.contentMediaSession?.token?.value;
		if (token) {
			const { exp } = jwtDecode<JwtPayload>(token);
			const currentTime = Math.floor(Date.now() / 1000);
			// Check if the token has not expired
			if (exp > currentTime) {
				return { data };
			}
		}
	} catch {
		// Fall through to refetch on decode failure
	}
	// Refetch with network-only policy if token is expired, or invalid
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	return client.query<MediaUploadTokenQueryT, MediaUploadTokenQueryVariables>({
		query: MediaUploadTokenQuery,
		variables: { contentId },
		fetchPolicy: 'network-only',
	});
};

export const getCommentUploadToken = async (contentId: string, cloudId: string) => {
	const client = getApolloClient();
	try {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const { data } = await client.query<
			CommentMediaUploadTokenQueryT,
			CommentMediaUploadTokenQueryVariables
		>({
			query: CommentMediaUploadTokenQuery,
			variables: { cloudId, contentId },
			fetchPolicy: 'cache-first',
		});
		const token = data?.contentMediaSession?.token?.value;
		if (token) {
			const { exp } = jwtDecode<JwtPayload>(token);
			const currentTime = Math.floor(Date.now() / 1000);
			// Check if the token has not expired
			if (exp > currentTime) {
				return { data };
			}
		}
	} catch {
		// Fall through to refetch on decode failure
	}
	// Refetch with network-only policy if token is expired, or invalid
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	return client.query<CommentMediaUploadTokenQueryT, CommentMediaUploadTokenQueryVariables>({
		query: CommentMediaUploadTokenQuery,
		variables: { cloudId, contentId },
		fetchPolicy: 'network-only',
	});
};

export const getIsExportEnabledForContent = (contentId: string, spaceKey?: string) => {
	let variables: DSPIsActionEnabledForContentQueryVariables = {
		action: DataSecurityPolicyAction.PAGE_EXPORT,
		contentId,
		contentStatus: 'CURRENT' as DataSecurityPolicyDecidableContentStatus,
		contentVersion: 1,
	};
	if (spaceKey) {
		variables = { ...variables, spaceKey };
	}
	/* Note -> relay compatible query replacement available, in @confluence/data-security package.
	 * Leaving as is for now, to preserve consistent behavior w/ this direct getApolloClient() call
	 */
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	return getApolloClient().query<
		DSPIsActionEnabledForContentQueryT,
		DSPIsActionEnabledForContentQueryVariables
	>({
		query: DSPIsActionEnabledForContentQuery,
		variables,
		context: {
			allowOnExternalPage: true,
		},
		fetchPolicy: 'cache-first',
	});
};
